import type { Post } from '@mg/shared';
import { ContentKind } from '@mg/shared';
import { GenerateDesignModal } from './GenerateDesignModal';

/**
 * Extract a design-ready line from a post's body copy.
 *
 * A post's TEXT block runs to thousands of characters; a design overlay is one line. So this takes the
 * FIRST SENTENCE, caps it at eight words and strips trailing punctuation — the rule the prototype
 * used, and a reasonable one: the opening sentence of a post is almost always its claim.
 *
 * It is a STARTING POINT, not an answer, which is why the field stays editable and says where the text
 * came from. Pasting three thousand characters into a headline field would be worse than pasting
 * nothing.
 */
export function designHeadlineFromPost(post: Post | null): string {
  if (!post) return '';
  const body = (post.contents ?? [])
    .filter((c) => c.kind === ContentKind.TEXT && c.text?.trim())
    .map((c) => (c.text ?? '').trim())
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
  // The post title is the fallback rather than an empty field — it is the one line a post always has.
  if (!body) return post.title?.trim() ?? '';

  // First sentence: up to the first ., ! or ? that is followed by a space or the end. Markdown
  // headings and list markers are stripped first, since a body that opens with "## Why this matters"
  // should yield the words, not the hashes.
  const cleaned = body.replace(/^[#>\-*\s]+/, '');
  const sentence = /^(.*?[.!?])(\s|$)/.exec(cleaned)?.[1] ?? cleaned;
  const words = sentence.split(' ').filter(Boolean).slice(0, 8).join(' ');
  return words.replace(/[.,;:!?\-–—]+$/, '').trim();
}

/**
 * Generate Design, opened from a post.
 *
 * An adapter, so all four call sites keep the `{post, onClose}` shape they had with the design bundle
 * — and so the headline-extraction rule lives in exactly one place rather than being reimplemented at
 * each of them.
 *
 * Keyed off the SAVED post, like the bundle was: an unsaved edit is not in the design, and it should
 * not look as though it is.
 */
export function PostGenerateDesignModal({
  post,
  onClose,
}: {
  /** The post to seed from. Null keeps the dialog closed. */
  post: Post | null;
  onClose: () => void;
}) {
  return (
    <GenerateDesignModal
      open={post != null}
      onClose={onClose}
      seed={{ text: designHeadlineFromPost(post), fromPost: true }}
    />
  );
}
